// ── EMAIL PANELS ──────────────────────────────────────────────────────────────
// Components that generate AI-drafted vendor emails.
// EmailPanel, COMEmailModal, ChildQuoteSection,
// ComponentQuotesPanel.

import React, { useState } from 'react';
import { STATUSES, computeSidemark } from '../utils';
import { Btn, Sel } from './ui';
import { BLANK_COM } from './panels';
import { callClaude, dateStr } from './helpers';

// ── Constants (also defined in ProcurementApp.jsx — shared until extracted) ──
const AUSTIN_EMAIL = "purchasing@cimonstarr.com";

const SCHEDULE_FIELDS = {
  "Furniture":[],
  "Lighting":[
    {key:"technology",label:"Technology",type:"text"},
    {key:"bulb",label:"Bulb",type:"text"},
    {key:"base",label:"Base",type:"text"},
    {key:"wattage",label:"Wattage",type:"text"},
    {key:"lumens",label:"Lumens",type:"text"},
    {key:"kelvins",label:"Kelvins",type:"text"},
    {key:"cri",label:"CRI",type:"text"},
    {key:"voltage",label:"Voltage",type:"text"},
    {key:"circuitry",label:"Circuitry",type:"text"},
  ],
  "Plumbing / Fixtures":[
    {key:"roughInNotes",label:"Rough-in Notes",type:"text"},
    {key:"gpm",label:"GPM / Flow Rate",type:"text"},
    {key:"voltage",label:"Voltage",type:"text"},
  ],
  "Art & Objects":[
    {key:"artistMaker",label:"Artist / Maker",type:"text"},
    {key:"medium",label:"Medium",type:"text"},
    {key:"framingMount",label:"Framing / Mount",type:"text"},
  ],
  "Hardware":[
    {key:"material",label:"Material",type:"text"},
    {key:"application",label:"Application",type:"text"},
  ],
  "Equipment":[
    {key:"voltageFuel",label:"Voltage / Fuel Type",type:"text"},
    {key:"installNotes",label:"Install Notes",type:"textarea"},
  ],
  "Tile & Stone":[
    {key:"material",label:"Material",type:"text"},
    {key:"size",label:"Tile Size",type:"text"},
    {key:"thickness",label:"Thickness",type:"text"},
    {key:"coverageNotes",label:"Coverage / Qty Notes",type:"text"},
    {key:"groutSpec",label:"Grout Spec",type:"text"},
  ],
  "Finish Schedule":[
    {key:"type",label:"Type",type:"select",options:["Paint","Wallpaper","Wall Covering","Flooring","Tile","Stone","Other"]},
    {key:"space",label:"Space",type:"text"},
    {key:"element",label:"Element",type:"text"},
    {key:"collection",label:"Collection",type:"text"},
    {key:"colorPattern",label:"Color / Pattern",type:"text"},
    {key:"colorNumber",label:"Color / Product Number",type:"text"},
    {key:"sheenFinish",label:"Sheen / Finish",type:"text"},
    {key:"coverage",label:"Coverage",type:"text"},
    {key:"applicationArea",label:"Application Area",type:"text"},
  ],
  "Window Treatments":[
    {key:"style",label:"Style",type:"select",options:["Ripple Fold","Pinch Pleat","Flat Panel","Goblet Pleat","Inverted Box Pleat","Roman Shade","Roller Shade","Woven Wood","Sheer Panel"]},
    {key:"draw",label:"Draw",type:"select",options:["Stack Right","Stack Left","Center Split"]},
    {key:"mountType",label:"Mount Type",type:"select",options:["Inside Mount","Outside Mount","Ceiling Mount"]},
    {key:"controlType",label:"Control Type",type:"select",options:["Manual","Motorized","Automated"]},
    {key:"lining",label:"Lining",type:"select",options:["Unlined","Lined","Blackout","Interlining","Interlining + Blackout"]},
    {key:"collection",label:"Fabric Collection",type:"text",placeholder:"e.g. Schumacher Celerie"},
    {key:"patternColorway",label:"Pattern / Colorway",type:"text",placeholder:"e.g. Leaf Weave / Ivory"},
    {key:"content",label:"Content",type:"text",placeholder:"e.g. 100% Linen"},
    {key:"fabricWidth",label:"Fabric Width",type:"text",placeholder:'e.g. 54"'},
    {key:"repeatH",label:"Repeat H",type:"text",placeholder:'e.g. 13.5"'},
    {key:"repeatV",label:"Repeat V",type:"text",placeholder:'e.g. 13.5"'},
    {key:"yardage",label:"Yardage",type:"text",placeholder:"e.g. 12 yards"},
    {key:"cutDirection",label:"Cut Direction",type:"text",placeholder:"e.g. Railroaded or Straight cut"},
    {key:"comInstructions",label:"COM Instructions",type:"textarea",placeholder:"e.g. Match repeat at center seam, double fullness..."},
    {key:"trimDescription",label:"Trim Description",type:"text",placeholder:"e.g. Samuel & Sons tape trim, 1.5in"},
    {key:"trimSku",label:"Trim SKU / Pattern",type:"text",placeholder:"e.g. STL-1254"},
    {key:"trimColorway",label:"Trim Colorway",type:"text",placeholder:"e.g. Antique Gold"},
    {key:"trimYardage",label:"Trim Yardage",type:"text",placeholder:"e.g. 8 yards"},
    {key:"hardwareDescription",label:"Hardware Description",type:"text",placeholder:"e.g. Lutron Caseta motor, rod + brackets"},
    {key:"hardwareSku",label:"Hardware SKU / Model",type:"text",placeholder:"e.g. Lutron RRD-6CL-WH"},
    {key:"workroomName",label:"Ship to Workroom",type:"text",placeholder:"e.g. ABC Drapery Workroom"},
    {key:"shipToAddress",label:"Workroom Address",type:"text",placeholder:"e.g. 123 Main St, San Francisco CA 94103"},
  ],
  "Cushions & Throw Pillows":[
    {key:"cushionSource",label:"Source",type:"select",options:["Custom / Workroom","Ready-Made"]},
    {key:"fabricFace",label:"Face Fabric",type:"text"},
    {key:"fabricBack",label:"Back Fabric",type:"text"},
    {key:"fillType",label:"Fill / Insert Type",type:"select",options:["Down & Feather","Down Alternative","Foam","Kapok","Other"]},
    {key:"closure",label:"Closure",type:"select",options:["Invisible Zip","Envelope","Button","Hook & Loop","Sewn Closed"]},
    {key:"welt",label:"Welt / Trim",type:"text"},
    {key:"flangeWidth",label:"Flange Width",type:"text"},
    {key:"finishedSize",label:"Finished Size",type:"text"},
  ],
  // Softgoods (2026-09-08) — bedding, towels, linens. No bespoke spec
  // panel by design (Cimon: "basic specs on the main item list" —
  // dimension/size, finish, notes are core item fields), so it mirrors
  // Furniture: an empty field list that prints through the standard
  // template columns.
  "Softgoods":[],
  "Millwork":[
    {key:"material",label:"Material",type:"text"},
    {key:"speciesGrade",label:"Species / Grade",type:"text"},
    {key:"profileStyle",label:"Profile / Style",type:"text"},
    {key:"paintStainColor",label:"Paint / Stain Color",type:"text"},
    {key:"linearFootage",label:"Linear Footage",type:"text"},
    {key:"hardwareSpec",label:"Hardware Spec",type:"text"},
    {key:"installNotes",label:"Install Notes",type:"textarea"},
    {key:"shopDrawingRef",label:"Shop Drawing Ref",type:"text"},
  ],
};

export function EmailPanel(p) {
  var item = p.item; var vendors = p.vendors; var projects = p.projects;
  var es = useState(""); var email = es[0]; var setEmail = es[1];
  var ls = useState(false); var loading = ls[0]; var setLoad = ls[1];
  var cs = useState(false); var copied = cs[0]; var setCopied = cs[1];
  var sv = null;
  for (var i = 0; i < vendors.length; i++) { if (vendors[i].name === item.vendor) { sv = vendors[i]; break; } }
  var sp = null;
  for (var i = 0; i < projects.length; i++) { if (projects[i].label === item.project) { sp = projects[i]; break; } }
  var sidemark = computeSidemark(item.project, item.roomName, item.roomNumber);
  function fmtAddr(proj) {
    if (!proj || !proj.address) return "";
    return [proj.attn ? "Attn: " + proj.attn : "", proj.address, proj.address2, [proj.city, proj.state, proj.zip].filter(function(x) { return !!x; }).join(", ")].filter(function(x) { return !!x; }).join("\n");
  }
  function specsForEmail() {
    var fields = SCHEDULE_FIELDS[item.schedule] || [];
    return fields.filter(function(f) { return item.specs && item.specs[f.key]; }).map(function(f) { return f.label + ": " + item.specs[f.key]; }).join("\n") || "N/A";
  }
  function generate() {
    setLoad(true); setEmail("");
    var sys = "You are an assistant for Cimon Starr Interior Design, a high-end residential interior design firm in San Francisco. Write professional, concise vendor emails requesting quotes. Follow these rules exactly:\n\nFORMAT:\n- Subject line: [Item Name] — [Project Name only, no numbers]\n- Opening: I am writing to request a quote for the following item for our [Project] project.\n- List Item and Quantity, then a blank line\n- Specifications section: each field on its own line, no bold, format as Field: Value. Omit any fields that are empty.\n- Always include verbatim: Please provide itemized pricing including unit cost, freight, and any applicable tariffs. Please include lead time from payment received and payment terms.\n- SIDEMARK in all caps on its own line\n- If COM fabric is present: include a COM Note section. Each field on its own line. Label as Fabric: not COM fabric:. If yardage is known include it and ask them to confirm. If yardage is unknown ask them to specify yardage required based on pattern repeat. Always include: Please provide your receiving address for COM shipment. Always include: Please also advise if a memo for approval is required prior to ordering.\n- Ship To section if address provided\n- Sign off exactly: Please feel free to reach out with any questions. Thank you,\n- No signature name. No company name.\n\nNever use bold or markdown formatting in the email body. Never include pricing in COM notes.";
    var comKids = (item.children || []).filter(function(c) { return c.childType === "COM/COL Fabric"; });
    var comNote = comKids.map(function(c) {
      var com = c.com || {};
      var name = c.item || com.patternColorway || "";
      var source = [com.manufacturer, com.collection].filter(function(x) { return !!x; }).join(", ");
      var yardLine = com.yardage ? "Yardage: " + com.yardage + " — please confirm." : "Please specify yardage required based on pattern repeat.";
      return "Fabric: " + name + (source ? " (" + source + ")" : "") + "\n" + yardLine + "\nPlease provide your receiving address for COM shipment.\nPlease also advise if a memo for approval is required prior to ordering.";
    }).join("\n\n");
    var _vc = sv && ((sv.contacts && sv.contacts[0]) || { name: sv.contact || "", email: sv.email || "" });
    var vContact = _vc && _vc.name ? ", attention " + _vc.name : "";
    var vEmail = _vc && _vc.email ? " (" + _vc.email + ")" : "";
    var addr = fmtAddr(sp);
    var usr = "Quote request email to " + (item.vendor || "[Vendor]") + vContact + vEmail + ".\nItem: " + (item.productName || item.item || "") + "\nQty: " + (item.qty || "1") + " " + (item.qtyType || "") + "\n\nSpecifications:\n" + specsForEmail() + (comNote ? "\n\nCOM Note:\n" + comNote : "") + "\nProject: " + (item.project || "N/A") + "\nSIDEMARK: " + sidemark + "\nNotes: " + (item.notes || "None") + (addr ? "\n\nShip To:\n" + addr : "");
    callClaude(sys, usr).then(function(result) { setEmail(result); setLoad(false); });
  }
  var emailDisplay = null;
  if (email) {
    emailDisplay = (
      <>
        <div style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', maxHeight: 420, overflowY: 'auto', color: '#222', marginTop: 12, marginBottom: 10 }}>
          {email}
        </div>
        <Btn variant="secondary" onClick={function() { navigator.clipboard.writeText(email); setCopied(true); setTimeout(function() { setCopied(false); }, 2000); }}>
          {copied ? 'v Copied!' : 'Copy to Clipboard'}
        </Btn>
      </>
    );
  }
  return (
    <div>
      <Btn onClick={generate} disabled={!item.vendor || !item.item || loading}>
        {loading ? 'Drafting...' : 'Generate Quote Request Email'}
      </Btn>
      {emailDisplay}
    </div>
  );
}


export function COMEmailModal(p) {
  var child = p.child; var vendors = p.vendors;
  var ts = useState("fabric"); var target = ts[0]; var setTarget = ts[1];
  var es = useState(""); var email = es[0]; var setEmail = es[1];
  var ls = useState(false); var loading = ls[0]; var setLoad = ls[1];
  var cs = useState(false); var copied = cs[0]; var setCopied = cs[1];
  var effectiveVendor = child.vendor || child.vendorCustom || "";
  var vd = null;
  for (var i = 0; i < vendors.length; i++) { if (vendors[i].name === child.vendor) { vd = vendors[i]; break; } }
  var com = child.com || BLANK_COM;
  var _stv = null;
  if (child.shipToVendorId) {
    for (var _vi = 0; _vi < vendors.length; _vi++) {
      if (String(vendors[_vi].id) === String(child.shipToVendorId)) { _stv = vendors[_vi]; break; }
    }
  }
  var comAddr = _stv
    ? [_stv.name, _stv.address, _stv.address2, [_stv.city, _stv.state, _stv.zip].filter(function(x) { return !!x; }).join(", ")].filter(function(x) { return !!x; }).join("\n")
    : [com.shipToName, com.shipToAttn ? "Attn: " + com.shipToAttn : "", com.shipToAddress, com.shipToAddress2, [com.shipToCity, com.shipToState, com.shipToZip].filter(function(x) { return !!x; }).join(", ")].filter(function(x) { return !!x; }).join("\n");
  function generate() {
    setLoad(true); setEmail("");
    var sys = "You are an assistant for Cimon Starr Interior Design. Write professional, precise vendor emails. Voice: elevated, direct, concise. Always include SIDEMARK in all caps. Never include fabric pricing. Sign off exactly: Please feel free to reach out with any questions. Thank you, — no name, no company.";
    var vContact = vd && vd.contact ? ", attention " + vd.contact : "";
    var vEmail = vd && vd.email ? " (" + vd.email + ")" : "";
    var usr;
    if (target === "fabric") {
      usr = "Write a COM shipment instruction email to " + effectiveVendor + vContact + vEmail + ".\nFabric: " + (com.patternColorway || child.item || "") + " " + ([com.manufacturer, com.collection].filter(function(x) { return !!x; }).join(", ")) + "\nContent: " + (com.content || "\u2014") + ", Width: " + (com.width || "\u2014") + "\nRepeat: H " + (com.repeatH || "\u2014") + " x V " + (com.repeatV || "\u2014") + "\nYardage: " + (com.yardage || "\u2014") + "\nCut Direction: " + (com.cutDirection || "\u2014") + "\nCOM Instructions: " + (com.comInstructions || "None") + "\nApplied to: " + p.parentItem + "\nSIDEMARK: " + p.sidemark + "\nShip to:\n" + (comAddr || "[Manufacturer receiving address not yet entered]");
    } else {
      usr = "Write an email to " + effectiveVendor + vContact + vEmail + " confirming COM acceptance.\nFurniture piece: " + p.parentItem + "\nFabric: " + (com.patternColorway || child.item || "") + " " + ([com.manufacturer, com.collection].filter(function(x) { return !!x; }).join(", ")) + "\nContent: " + (com.content || "\u2014") + ", Width: " + (com.width || "\u2014") + "\nYardage: " + (com.yardage || "TBD") + "\nSIDEMARK: " + p.sidemark + "\nAsk them to confirm COM acceptance, receiving address, labeling requirements, lead time once COM received, and whether a memo for approval is required.";
    }
    callClaude(sys, usr).then(function(result) { setEmail(result); setLoad(false); });
  }
  var emailDisplay = null;
  if (email) {
    emailDisplay = (
      <div style={{ marginTop: 14 }}>
        <div style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8, padding: 14, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', maxHeight: 340, overflowY: 'auto', color: '#222' }}>
          {email}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
          <Btn variant="secondary" onClick={function() { navigator.clipboard.writeText(email); setCopied(true); setTimeout(function() { setCopied(false); }, 2000); }}>
            {copied ? 'v Copied!' : 'Copy to Clipboard'}
          </Btn>
        </div>
      </div>
    );
  }
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={p.onClose}
    >
      <div
        style={{ background: '#fff', borderRadius: 12, padding: 28, width: 660, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,.2)' }}
        onClick={function(e) { e.stopPropagation(); }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Generate COM Email</div>
          <button onClick={p.onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#999' }}>x</button>
        </div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>
          {child.item || child.childType} linked to: {p.parentItem}
        </div>
        <Sel label="Email purpose" value={target} onChange={setTarget}>
          <option value="fabric">To fabric vendor — ship COM to manufacturer</option>
          <option value="mfr">To furniture manufacturer — confirm COM acceptance</option>
        </Sel>
        <Btn onClick={generate} disabled={loading}>
          {loading ? 'Drafting...' : 'Generate Email'}
        </Btn>
        {emailDisplay}
      </div>
    </div>
  );
}

export function ChildQuoteSection(p) {
  var child = p.child; var parentItem = p.parentItem; var sidemark = p.sidemark; var vendors = p.vendors;
  var es = useState(""); var email = es[0]; var setEmail = es[1];
  var ls = useState(false); var loading = ls[0]; var setLoad = ls[1];
  var cs = useState(false); var copied = cs[0]; var setCopied = cs[1];
  var os = useState(true); var open = os[0]; var setOpen = os[1];
  var typeColors = { "Fabric": ["#7b1fa2", "#fdf6ff", "#e1bee7"], "COM/COL Fabric": ["#7b1fa2", "#fdf6ff", "#e1bee7"], "Trim": ["#e65100", "#fff8e1", "#ffe082"], "Hardware": ["#2e7d32", "#e8f5e9", "#a5d6a7"], "Workroom": ["#1565c0", "#e3f2fd", "#90caf9"] };
  var tc = typeColors[child.childType] || ["#666", "#fafafa", "#e0e0e0"];
  var vd = (vendors || []).find(function(v) { return v.name === child.vendor; }) || null;
  function fmtSpecs() {
    var specs = child.specs || {};
    var LABELS = { treatmentType: "Treatment Type", motorized: "Motorized", motorBrand: "Motor Brand", mountType: "Mount Type", stackingPreference: "Stacking Preference", lined: "Lined", liningType: "Lining Type", interlining: "Interlining", pleatStyle: "Pleat Style", pleatSpacing: "Pleat Spacing", returnDepth: "Return Depth", rodHeight: "Rod Height from Floor", finishedWidth: "Finished Width", finishedLength: "Finished Length", panels: "Number of Panels", rings: "Rings", topTreatment: "Top Treatment", hwType: "Hardware Type", finish: "Finish", diameter: "Diameter", patternColorway: "Pattern/Colorway", manufacturer: "Manufacturer", collection: "Collection", content: "Content", width: "Width", repeatH: "H Repeat", repeatV: "V Repeat", yardage: "Yardage", cutDirection: "Cut Direction", comInstructions: "COM Instructions", style: "Style", colorway: "Colorway", length: "Length", shadeType: "Shade Type", opacity: "Opacity", rollType: "Roll Type", motor: "Motorized", bottomBar: "Bottom Bar", fascia: "Fascia" };
    return Object.keys(specs).filter(function(k) { return specs[k]; }).map(function(k) { return (LABELS[k] || k) + ": " + specs[k]; }).join("\n") || "N/A";
  }
  function generate() {
    setLoad(true); setEmail("");
    var sys = "You are an assistant for Cimon Starr Interior Design, a high-end residential interior design firm in San Francisco. Write professional, concise vendor emails requesting quotes for individual components of a larger installation. Format rules:\n- Subject line: [Component Type] for [Parent Item] — [Project Name only, no numbers]\n- Opening: state this is a quote request for a specific component\n- Component item and quantity on their own lines\n- Specifications section: each field on its own line, no bold, Field: Value format. Omit empty fields.\n- Always include: Please provide itemized pricing, freight if applicable, and any applicable tariffs. Please include lead time from payment received and payment terms.\n- SIDEMARK in all caps on its own line\n- Sign off exactly: Please feel free to reach out with any questions. Thank you,\n- No signature name. No company name. No markdown.";
    var vContact = vd && vd.contact ? ", attention " + vd.contact : "";
    var vEmail = vd && vd.email ? " (" + vd.email + ")" : "";
    var usr = "Quote request email for a " + child.childType + " component.\n\nTo: " + (child.vendor || "[Vendor]") + vContact + vEmail + "\nComponent: " + (child.item || child.childType) + "\nQty: " + (child.qty || "1") + " " + (child.qtyType || "") + "\n\nFor parent item: " + (parentItem.item || "") + (parentItem.productName ? " (" + parentItem.productName + ")" : "") + "\nProject: " + (parentItem.project || "N/A") + "\nRoom: " + (parentItem.roomName || "N/A") + (parentItem.roomNumber ? " #" + parentItem.roomNumber : "") + "\nSIDEMARK: " + sidemark + "\n\nSpecifications:\n" + fmtSpecs() + (child.notes ? "\n\nNotes: " + child.notes : "");
    callClaude(sys, usr).then(function(result) { setEmail(result); setLoad(false); }).catch(function() { setLoad(false); });
  }
  return (
    <div style={{ border: '1px solid ' + tc[2], borderRadius: 8, marginBottom: 12, borderLeft: '3px solid ' + tc[0], overflow: 'hidden' }}>
      <div
        style={{ background: tc[1], padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={function() { setOpen(!open); }}
      >
        <div style={{ fontWeight: 700, fontSize: 13, color: tc[0] }}>
          {child.childType}{child.vendor ? ' — ' + child.vendor : ''}{child.item ? ' · ' + child.item : ''}
        </div>
        <div style={{ fontSize: 11, color: tc[0] }}>{open ? '▲' : '▼'}</div>
      </div>
      {open && (
        <div style={{ padding: 14 }}>
          {!child.vendor && (
            <div style={{ fontSize: 12, color: '#aaa', marginBottom: 8 }}>No vendor assigned — add a vendor on the Details tab to generate a quote email.</div>
          )}
          <Btn onClick={generate} disabled={!child.vendor || !child.item || loading}>
            {loading ? 'Drafting...' : 'Generate Quote Email'}
          </Btn>
          {email && (
            <>
              <div style={{ background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', maxHeight: 380, overflowY: 'auto', color: '#222', marginTop: 12, marginBottom: 10 }}>
                {email}
              </div>
              <Btn variant="secondary" onClick={function() { navigator.clipboard.writeText(email); setCopied(true); setTimeout(function() { setCopied(false); }, 2000); }}>
                {copied ? '\u2713 Copied!' : 'Copy to Clipboard'}
              </Btn>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function ComponentQuotesPanel(p) {
  var item = p.item; var children = p.children || []; var vendors = p.vendors;
  var sidemark = computeSidemark(item.project, item.roomName, item.roomNumber);
  if (!children.length) return (
    <div style={{ padding: 24, textAlign: 'center', color: '#aaa', fontSize: 13 }}>No component items linked.</div>
  );
  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>Generate quote request emails for each component. Each email targets the component vendor independently.</div>
      {children.map(function(c) {
        return <ChildQuoteSection key={c.id} child={c} parentItem={item} sidemark={sidemark} vendors={vendors} />;
      })}
    </div>
  );
}
